import { Match } from 'effect';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import type { AnalysisRequest, Portfolio, RangeAnalysisResponse, RangeDebugResponse } from 'venn-api';
import {
  AnalysisSubject,
  type AsOfDateSetting,
  getLastUploadedDate,
  isPortfolioLabDisabledForProFormaPortfolio,
  pruneNodesWithZeroAllocation,
} from 'venn-utils';
import { proxySetIdState } from 'venn-state';
import { useHistoricalPortfolioSnapshot } from '../../../../venn-utils/src/historical-portfolio/useHistoricalPortfolioSnapshot';
import { useRangeAnalysisQuery } from '../../hooks/useRangeAnalysisQuery';
import { augmentPortfolioWithRangeAnalysis } from '../allocation-panel-utils';
import type {
  AllocationPanelContextValue,
  HistoricalPortfolioCommon,
  HistoricalPortfolioContextValue,
} from '../AllocationPanelContext';
import { useIsLeverageShort } from '../../hooks';

const getAllocationPanelContextFromNetworkResponse = ({
  analysisRequest,
  rangeDebug,
  rangeAnalysis,
  snapshot,
  historicalPortfolioCommon,
}: {
  analysisRequest: Partial<AnalysisRequest>;
  rangeDebug: RangeDebugResponse;
  rangeAnalysis: RangeAnalysisResponse;
  snapshot: Portfolio;
  historicalPortfolioCommon: HistoricalPortfolioCommon;
}): HistoricalPortfolioContextValue => {
  const primaryIndex = analysisRequest.subjects?.findIndex((s) => s.comparisonType === 'PRIMARY');
  const primaryRangeAnalysis = !isNil(primaryIndex) ? rangeAnalysis.rangeAnalyses[primaryIndex] : undefined;
  const allocationDates = rangeDebug?.historicalPortfolioMetadata?.allSortedAnalysisAllocationDates;
  const lastUploadedAllocationsDate = getLastUploadedDate(
    rangeDebug?.historicalPortfolioMetadata?.allSortedRebalancingDates,
  );
  const frequency = rangeDebug?.maxFrequency;
  if (
    !allocationDates ||
    allocationDates.length === 0 ||
    !lastUploadedAllocationsDate ||
    !frequency ||
    !primaryRangeAnalysis
  ) {
    return {
      status: 'error' as const,
      errorType: rangeDebug?.insufficientRange ? ('insufficient overlap with returns' as const) : undefined,
      ...historicalPortfolioCommon,
    };
  }
  const augmentedPortfolio = augmentPortfolioWithRangeAnalysis({
    portfolio: snapshot,
    rangeAnalysis: primaryRangeAnalysis,
  });
  const filteredPortfolio = pruneNodesWithZeroAllocation(augmentedPortfolio);

  return {
    ...historicalPortfolioCommon,
    status: 'success' as const,
    portfolio: filteredPortfolio,
    allocationDates,
    lastUploadedAllocationsDate,
    currentAsOfDate: rangeDebug.maxEndDate,
    frequency,
  };
};
export const useAllocationPanelContext = ({
  portfolio,
  allocationsAsOfDate,
  setAllocationsAsOfDate,
}: {
  portfolio: Portfolio | undefined;
  allocationsAsOfDate: AsOfDateSetting;
  setAllocationsAsOfDate: (asOfDate: AsOfDateSetting) => void;
}): AllocationPanelContextValue => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const analysisSubject = portfolio ? new AnalysisSubject(portfolio, 'portfolio', proxySetId) : undefined;
  const { rangeDebugResponse, allocationsAsOfDateTimestamp, historicalPortfolioSnapshotResponse } =
    useHistoricalPortfolioSnapshot({
      analysisSubject,
      asOfDateSetting: allocationsAsOfDate,
    });
  const rangeAnalysisResponse = useRangeAnalysisQuery(analysisSubject);
  const isLeverageShortPortfolio = useIsLeverageShort(portfolio);
  const isPortfolioLabDisabled = isPortfolioLabDisabledForProFormaPortfolio(portfolio);

  return useMemo(() => {
    if (!portfolio || !portfolio.historical) {
      return {
        allocationPanelType: 'full',
        isLeverageShortPortfolio,
        isPortfolioLabDisabled,
        aggregatedPortfolioSettings: portfolio?.aggregatedPortfolioSettings,
      };
    }

    const historicalPortfolioCommon = {
      allocationPanelType: 'historical read-only' as const,
      allocationsAsOfDate,
      allocationsAsOfDateTimestamp,
      setAllocationsAsOfDate,
      isAllocationsForwardFilled: !!rangeDebugResponse.data?.historicalPortfolioMetadata?.isAllocationsForwardFilled,
    };

    return Match.value([rangeDebugResponse, historicalPortfolioSnapshotResponse, rangeAnalysisResponse]).pipe(
      Match.withReturnType<AllocationPanelContextValue>(),
      Match.when(
        [{ status: 'success' }, { status: 'success' }, { status: 'success' }],
        ([{ data: rangeDebug }, { data: snapshot }, { rangeAnalysis, analysisRequest }]) =>
          getAllocationPanelContextFromNetworkResponse({
            analysisRequest,
            rangeDebug,
            rangeAnalysis,
            snapshot,
            historicalPortfolioCommon,
          }),
      ),
      Match.whenOr(
        [{ status: 'error' }, Match.any, Match.any],
        [Match.any, { status: 'error' }, Match.any],
        [Match.any, Match.any, { status: 'error' }],
        () => {
          return {
            status: 'error' as const,
            errorType: rangeDebugResponse.data?.insufficientRange
              ? ('insufficient overlap with returns' as const)
              : undefined,
            ...historicalPortfolioCommon,
          };
        },
      ),
      Match.whenOr(
        [{ status: 'loading' }, Match.any, Match.any],
        [Match.any, { status: 'loading' }, Match.any],
        [Match.any, Match.any, { status: 'loading' }],
        () => {
          return {
            status: 'loading' as const,
            ...historicalPortfolioCommon,
          };
        },
      ),
      Match.orElseAbsurd,
    );
  }, [
    portfolio,
    allocationsAsOfDate,
    allocationsAsOfDateTimestamp,
    setAllocationsAsOfDate,
    rangeDebugResponse,
    historicalPortfolioSnapshotResponse,
    rangeAnalysisResponse,
    isLeverageShortPortfolio,
    isPortfolioLabDisabled,
  ]);
};
